import { useState } from 'react';
import PropTypes from 'prop-types';
// material

import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Avatar
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { parseValidationErrorMessage, getLanguageLetter } from '../../../utils/Utilities';
import { AddLanguage } from '../../../network/Language';
import { APP_CONSTANTS } from '../../../URLConstants';
import { useAuth } from '../../customContextProviders/AuthContext';
import { SnackBarAlert } from '../../HelperComponents';

// ----------------------------------------------------------------------

const style = {
  width: '100%',
  maxWidth: { sm: '90%', xs: '90%', md: 500 },
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const UserSchema = Yup.object().shape({
  organization: Yup.string().required('Required'),
  language: Yup.string().required('Required'),
  languageUrl: Yup.string().required('Required')
});

AddLanguageToOrganizationModal.propTypes = {
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  _onAction: PropTypes.func
};

export default function AddLanguageToOrganizationModal({
  opneAssignedModal,
  handleClose,
  _onAction
}) {
  const { sessionUserData, allOrganizationList } = useAuth();

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const addLanguageFormik = useFormik({
    initialValues: {
      organization: '',
      language: '',
      languageUrl: ''
    },
    validationSchema: UserSchema,
    onSubmit: () => {
      AddLanguage(
        addLanguageFormik?.values?.organization,
        addLanguageFormik?.values?.language, // Shortcode (e.g., "en")
        APP_CONSTANTS.DEVICE_LANGUAGE_OPTIONS[addLanguageFormik?.values?.language], // Fullname (e.g., "English")
        addLanguageFormik?.values?.languageUrl
      )
        .then((res) => {
          addLanguageFormik.setSubmitting(false);
          handleClose();
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: `${
              APP_CONSTANTS.DEVICE_LANGUAGE_OPTIONS[addLanguageFormik?.values?.language]
            } successfully added to your selected organization.`,
            severity: 'success'
          }));
          setIsSnackBarAlertOpen(true);

          handleClose();
          addLanguageFormik.resetForm();
        })
        .catch((err) => {
          console.log(err);
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: parseValidationErrorMessage(err),
            severity: 'error'
          }));
          setIsSnackBarAlertOpen(true);

          handleClose();
          addLanguageFormik.resetForm();
        });
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = addLanguageFormik;

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={() => {
          handleClose();
          addLanguageFormik.resetForm();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <FormikProvider enableReinitialize="true" value={addLanguageFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                Add New Language
              </Typography>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel id="device-org-select-label">Organization </InputLabel>
                    <Select
                      labelId="device-org-select-label"
                      id="device-org-select"
                      sx={{ textAlign: 'left' }}
                      value={addLanguageFormik?.values.organization}
                      label="Organization"
                      onChange={addLanguageFormik.handleChange}
                      {...getFieldProps('organization')}
                      error={Boolean(touched.organization && errors.organization)}
                      helperText={touched.organization && errors.organization}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // Set a max height for the dropdown
                            width: 300 // Ensure the dropdown width matches the select width
                          }
                        }
                      }}
                    >
                      {allOrganizationList?.data &&
                        Array.isArray(allOrganizationList.data) &&
                        allOrganizationList.data
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((row) => (
                            <MenuItem key={row?.id} value={row?.id} sx={{ p: 1.5 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {row.image && (
                                  <img
                                    src={row.image}
                                    alt={row.name}
                                    style={{ width: 24, height: 24, marginRight: 8 }}
                                  />
                                )}
                                {row.name}
                              </Box>
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel id="device-language">Language</InputLabel>
                    <Select
                      labelId="device-language"
                      id="device-language-select"
                      label="Language"
                      onChange={addLanguageFormik.handleChange}
                      {...getFieldProps('language')}
                      error={Boolean(touched.language && errors.language)}
                      helperText={touched.language && errors.language}
                    >
                      {Object.entries(APP_CONSTANTS?.DEVICE_LANGUAGE_OPTIONS)?.map(
                        ([key, value]) => (
                          <MenuItem key={key} value={key}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                sx={{ bgcolor: '#fa7c05', width: 24, height: 24, fontSize: '1rem' }}
                              >
                                {getLanguageLetter(key)}
                              </Avatar>
                              <Typography>{value}</Typography>
                            </Box>
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Language URL"
                      id="languageUrl"
                      name="languageUrl"
                      variant="outlined"
                      {...getFieldProps('languageUrl')}
                      error={Boolean(touched.languageUrl && errors.languageUrl)}
                      helperText={touched.languageUrl && errors.languageUrl}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={addLanguageFormik.isSubmitting}
                  sx={{ mt: 2, height: '38px' }}
                  variant="contained"
                  type="submit"
                >
                  Add
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
